import React from "react";
import { createPortal } from "react-dom";
import { Loader2, FileText } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { carTestLabels } from "@/i18n";

interface PdfGenerationDialogProps {
  isOpen: boolean;
  testName?: string;
  fileName?: string;
  title?: string;
  message?: string;
  fileLabel?: string;
  pleaseWait?: string;
}

/** Non-modal overlay (avoids Radix Dialog leaving a blocking layer on Capacitor). */
export const PdfGenerationDialog: React.FC<PdfGenerationDialogProps> = ({
  isOpen,
  testName,
  fileName,
  title: titleOverride,
  message: messageOverride,
  fileLabel: fileLabelOverride,
  pleaseWait: pleaseWaitOverride,
}) => {
  const { language } = useLanguage();
  const ct = carTestLabels(language);
  const pdf = ct?.pdf as
    | {
        generatingTitle?: string;
        generatingFor?: string;
        generatingFallback?: string;
        fileLabel?: string;
        pleaseWait?: string;
      }
    | undefined;

  if (!isOpen) return null;

  const title = titleOverride ?? pdf?.generatingTitle ?? "Genererar PDF";
  const message =
    messageOverride ??
    (testName
      ? (pdf?.generatingFor ?? "Genererar PDF för {name}").replace(
          "{name}",
          testName,
        )
      : (pdf?.generatingFallback ?? "Genererar PDF..."));
  const fileLabel = fileLabelOverride ?? pdf?.fileLabel ?? "Fil:";
  const pleaseWait =
    pleaseWaitOverride ??
    pdf?.pleaseWait ??
    "Vänligen vänta medan PDF:en genereras...";

  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="w-full max-w-md rounded-xl border bg-background p-6 shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="h-5 w-5 text-blue-600 shrink-0" />
          <h2 className="text-lg font-semibold">{title}</h2>
        </div>
        <div className="flex flex-col items-center py-4 space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          <div className="text-center space-y-2">
            <p className="text-lg font-medium">{message}</p>
            {fileName && (
              <p className="text-sm text-muted-foreground">
                {fileLabel} {fileName}
              </p>
            )}
            <p className="text-sm text-muted-foreground">{pleaseWait}</p>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
